import { timezoneMap } from './timezoneMapping';

// build a lookup from an array of objects where each entry is an array grouped by key
const buildLookup = <T>(
  array: T[] = [],
  getKey: (item: T) => string
): Record<string, T[]> => array.reduce((acc, item) => {
  const key = getKey(item);
  if (!acc[key]) acc[key] = [];
  acc[key].push(item);
  return acc;
}, {} as Record<string, T[]>);

export {
    buildLookup
};

// Replace stylized Unicode letters with plain ASCII letters
export function unicodeToPlain(text: string): string {
  if (!text) return text;
  let result = '';
  for (const char of text) {
    const code = char.codePointAt(0);
    // Mathematical Bold Small a-z (U+1D41A–U+1D433)
    if (code! >= 0x1D41A && code! <= 0x1D433) result += String.fromCharCode(0x61 + (code! - 0x1D41A));
    // Mathematical Bold Capital A-Z (U+1D400–U+1D419)
    else if (code! >= 0x1D400 && code! <= 0x1D419) result += String.fromCharCode(0x41 + (code! - 0x1D400));
    // Mathematical Italic Small a-z (U+1D44E–U+1D467)
    else if (code! >= 0x1D44E && code! <= 0x1D467) result += String.fromCharCode(0x61 + (code! - 0x1D44E));
    // Mathematical Italic Capital A-Z (U+1D434–U+1D44D)
    else if (code! >= 0x1D434 && code! <= 0x1D44D) result += String.fromCharCode(0x41 + (code! - 0x1D434));
    // Mathematical Bold Italic Small a-z (U+1D482–U+1D49B)
    else if (code! >= 0x1D482 && code! <= 0x1D49B) result += String.fromCharCode(0x61 + (code! - 0x1D482));
    // Mathematical Bold Italic Capital A-Z (U+1D468–U+1D481)
    else if (code! >= 0x1D468 && code! <= 0x1D481) result += String.fromCharCode(0x41 + (code! - 0x1D468));
    // Mathematical Sans-Serif Bold Small a-z (U+1D5EE–U+1D607)
    else if (code! >= 0x1D5EE && code! <= 0x1D607) result += String.fromCharCode(0x61 + (code! - 0x1D5EE));
    // Mathematical Sans-Serif Bold Capital A-Z (U+1D5D4–U+1D5ED)
    else if (code! >= 0x1D5D4 && code! <= 0x1D5ED) result += String.fromCharCode(0x41 + (code! - 0x1D5D4));
    else result += char;
  }
  return result;
}

export function decodeHtmlEntities(str) {
  if (!str) return '';
  const txt = document.createElement('textarea');
  txt.innerHTML = str;
  return txt.value;
}

export function isEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!isEqual(a[i], b[i])) return false;
    }
    return true;
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    for (const key of keysA) {
      if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
      if (!isEqual(a[key], b[key])) return false;
    }
    return true;
  }
  return false;
}

// DateTime functions
export function getNextHourIsoLocal() {
  const now = new Date();
  now.setMinutes(0, 0, 0);
  now.setHours(now.getHours() + 1);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:00`;
}

export function roundToNext5Min(isoStr: string) {
  if (!isoStr) return '';
  const dt = new Date(isoStr);
  let minutes = dt.getMinutes();
  const remainder = minutes % 5;
  if (remainder !== 0) {
    minutes += 5 - remainder;
    if (minutes >= 60) {
      dt.setHours(dt.getHours() + 1);
      minutes = 0;
    }
    dt.setMinutes(minutes, 0, 0);
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

export function extractTimeZoneId(label: string | undefined): string {
  if (!label) return '';
  const idx = label.indexOf(' (');
  return idx > 0 ? label.slice(0, idx).trim() : label.trim();
}

export function matchTimezoneLabel(tzId: string | undefined, zoneLabels: string[]): string {
  if (!tzId || !Array.isArray(zoneLabels) || zoneLabels.length === 0) return '';
  return zoneLabels.find((lbl) => extractTimeZoneId(lbl) === tzId) || '';
}

export function findLabelAcrossRegions(tzId: string | undefined): string {
  if (!tzId) return '';
  for (const region of ['amer', 'emea', 'apjc'] as const) {
    const lbl = timezoneMap[region].find((z) => extractTimeZoneId(z) === tzId);
    if (lbl) return lbl;
  }
  return '';
}

export const formatDateTime = (value?: string | null) => {
  if (!value) return '-';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';

  return parsed.toLocaleString();
};

export const parseDateToMs = (value?: string | null): number => {
  if (!value) return 0;

  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const asArray = (data: any): any[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.items)) return data.items;
  return [];
}

export type ScreenSize = { width: number; height: number };

export const getScreenSize = (): ScreenSize => ({
  width: window.innerWidth,
  height: window.innerHeight,
});

/**
 * Subscribe to screen size changes (resize + orientation change).
 * Returns an unsubscribe function.
 */
export const onScreenSizeChange = (callback: (size: ScreenSize) => void) => {
  const handler = () => callback(getScreenSize());

  window.addEventListener("resize", handler, { passive: true });
  window.addEventListener("orientationchange", handler, { passive: true });

  // Fire once immediately so the caller gets the initial size
  handler();

  return () => {
    window.removeEventListener("resize", handler);
    window.removeEventListener("orientationchange", handler);
  };
};

export function formatDateOrTBD(v: any) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);

  if (d.getFullYear() < 2002) return 'TBD';

  return d.toLocaleDateString('en-US');
}

export const asSingleValue = (value: string | string[]) =>
  Array.isArray(value) ? (value[0] ?? '') : value.trim();

export const asMultiValue = (value: string | string[]) =>
  Array.isArray(value) ? value : value ? [value] : [];

export const asString = (value: unknown) => (value == null ? '' : String(value));

export function ensureNumber(v: unknown, fallback: number): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export function arraysEqual(a: unknown[], b: unknown[]): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; ++i) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

export type SEPhase =
  | 'discovery'
  | 'present'
  | 'design'
  | 'validate'
  | 'tech_close';

export const formatSEPhase = (phase?: SEPhase | string | null): string => {
  const normalized = String(phase ?? '')
    .trim()
    .toLowerCase();

  const phaseMap: Record<string, string> = {
    discovery: 'Discovery',
    present: 'Present',
    design: 'Design',
    validate: 'Validate',
    tech_close: 'Tech Close',
  };

  return phaseMap[normalized] ?? '';
};